import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Boton } from './basicos';

/**
 * Diálogo modal sobre el elemento nativo del navegador.
 *
 * Se usa `<dialog>` y no una capa propia porque el navegador ya resuelve bien lo difícil:
 * atrapa el foco adentro, cierra con Escape y deja el resto de la página inerte para los
 * lectores de pantalla. Reimplementar eso con divisiones es la forma más común de romper
 * la accesibilidad sin darse cuenta.
 */
export function Dialogo({
  abierto,
  titulo,
  descripcion,
  onCerrar,
  children,
  acciones,
}: {
  readonly abierto: boolean;
  readonly titulo: string;
  readonly descripcion?: string | undefined;
  readonly onCerrar: () => void;
  readonly children: ReactNode;
  readonly acciones?: ReactNode;
}): React.ReactElement {
  const referencia = useRef<HTMLDialogElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const dialogo = referencia.current;
    if (dialogo === null) return;
    if (abierto && !dialogo.open) dialogo.showModal();
    if (!abierto && dialogo.open) dialogo.close();
  }, [abierto]);

  return (
    <dialog
      ref={referencia}
      onClose={onCerrar}
      aria-label={titulo}
      className="w-full max-w-lg rounded-marco border border-borde bg-superficie p-0 text-texto backdrop:bg-black/40"
    >
      <div className="border-b border-borde px-4 py-3">
        <h2 className="text-sm font-semibold">{titulo}</h2>
        {descripcion === undefined ? null : (
          <p className="mt-0.5 text-xs text-texto-tenue">{descripcion}</p>
        )}
      </div>
      <div className="px-4 py-4">{children}</div>
      <div className="flex justify-end gap-2 border-t border-borde px-4 py-3">
        <Boton onClick={onCerrar}>{t('acciones.cancelar')}</Boton>
        {acciones}
      </div>
    </dialog>
  );
}
